import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import type { OdpovedTimeline } from '../api/JsdhNotifikaceService/generated/models/OdpovedTimeline';
import { formatujDobuReakce } from '../utils/statistikyUtils';

// Custom tooltip payload type based on recharts structure
interface TooltipPayloadEntry {
    color?: string;
    name?: string | number;
    value?: number | string;
}

interface CustomTooltipProps {
    payload?: ReadonlyArray<TooltipPayloadEntry>;
    label?: number | string;
}

interface TimelineChartProps {
    timeline: OdpovedTimeline[];
}

interface ChartDataPoint {
    cas: number;
    casLabel: string;
    jedu: number;
    nejedu: number;
    celkem: number;
}

const TimelineChart: React.FC<TimelineChartProps> = ({ timeline }) => {
    const theme = useTheme();

    const chartData = useMemo(() => {
        if (!timeline || timeline.length === 0) return [];

        const sorted = [...timeline].sort((a, b) => a.dobaReakceSekundy - b.dobaReakceSekundy);

        let jeduCount = 0;
        let nejeduCount = 0;

        const data: ChartDataPoint[] = [
            { cas: 0, casLabel: '0 s', jedu: 0, nejedu: 0, celkem: 0 }
        ];

        sorted.forEach(odpoved => {
            if (odpoved.odpoved === 'JEDU') {
                jeduCount++;
            } else {
                nejeduCount++;
            }

            data.push({
                cas: odpoved.dobaReakceSekundy,
                casLabel: formatujDobuReakce(odpoved.dobaReakceSekundy),
                jedu: jeduCount,
                nejedu: nejeduCount,
                celkem: jeduCount + nejeduCount,
            });
        });

        return data;
    }, [timeline]);

    if (!timeline || timeline.length === 0) {
        return (
            <Typography color="text.secondary" textAlign="center" py={4}>
                Žádná data pro zobrazení grafu
            </Typography>
        );
    }

    const CustomTooltip = ({ payload, label }: CustomTooltipProps) => {
        if (payload && payload.length > 0) {
            return (
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        p: 1.5,
                        border: 1,
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        boxShadow: 2,
                    }}
                >
                    <Typography variant="body2" fontWeight="bold">
                        Čas: {formatujDobuReakce(typeof label === 'number' ? label : 0)}
                    </Typography>
                    {payload.map((entry, index) => (
                        <Typography
                            key={index}
                            variant="body2"
                            sx={{ color: entry.color }}
                        >
                            {entry.name}: {entry.value}
                        </Typography>
                    ))}
                </Box>
            );
        }
        return null;
    };

    return (
        <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
                <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[300]} />
                    <XAxis
                        dataKey="cas"
                        tickFormatter={(value) => {
                            if (value < 60) return `${value}s`;
                            return `${Math.floor(value / 60)}m`;
                        }}
                        stroke={theme.palette.text.secondary}
                    />
                    <YAxis
                        allowDecimals={false}
                        stroke={theme.palette.text.secondary}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                        type="stepAfter"
                        dataKey="jedu"
                        name="JEDU"
                        stackId="1"
                        stroke={theme.palette.success.main}
                        fill={theme.palette.success.light}
                    />
                    <Area
                        type="stepAfter"
                        dataKey="nejedu"
                        name="NEJEDU"
                        stackId="1"
                        stroke={theme.palette.error.main}
                        fill={theme.palette.error.light}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default TimelineChart;
